import { Phone } from "@styled-icons/remix-fill/Phone";
import { ExternalLink } from "@styled-icons/remix-line/ExternalLink";
import { Flex, Grid, theme, Typography } from "antd";
import { DDPoint } from "haversine-ts";
import React from "react";

import {
  InlineChildren,
  LocationAddressStyled,
  LocationButtonStyled,
  LocationCtaStyled,
  LocationHoursStatusStyled,
  LocationHoursTextStyled,
  LocationNameStyled,
  NoWrapText,
} from "./styled";
import { LocationCardProps, LocationComponent } from "./types";

import { SolutionIcon } from "~/Atom/SolutionIcon";
import { SolutionSlug } from "~/Atom/SolutionIcon/types";
import { HaversineMiles, Miles, sortMarketsBySolutionSortOrder } from "~/utils";

export const LocationLink: LocationComponent = ({
  children,
  location: loc,
}) => {
  return (
    <a
      href={`/locations/${loc.slug}`}
      data-testid="location-card-name-link"
      target="_blank"
      rel="noreferrer"
      style={{
        // Webflow override
        fontWeight: "inherit",
      }}
    >
      {children}
    </a>
  );
};

export const LocationName: LocationComponent = ({ location: loc, popup }) => {
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();

  return (
    <Flex>
      <LocationNameStyled
        $token={token}
        $breakpoints={breakpoints}
        $popup={popup}
        data-testid="location-card-name"
      >
        {loc.name}
      </LocationNameStyled>
      {popup && !breakpoints.sm && <LocationIcons location={loc} />}
    </Flex>
  );
};

export const LocationNameLink: LocationComponent = ({
  location: loc,
  popup,
}) => {
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();

  return (
    <InlineChildren>
      <LocationNameStyled
        $token={token}
        $breakpoints={breakpoints}
        $popup={popup}
        data-testid="location-card-name-link"
      >
        <LocationLink location={loc}>{loc.name}</LocationLink>
      </LocationNameStyled>
      {popup && !breakpoints.sm && (
        <div>
          <LocationIcons location={loc} />
        </div>
      )}
    </InlineChildren>
  );
};

export const LocationIcons: LocationComponent = ({ location: loc }) => (
  <div style={{ display: "inline-block" }}>
    <Flex gap="small" align="center" justify="center">
      {loc.markets.sort(sortMarketsBySolutionSortOrder).map(({ solution }) => (
        <SolutionIcon
          key={solution.slug}
          solutionSlug={solution.slug as SolutionSlug}
          style={{
            width: "0.75rem",
            height: "0.75rem",
          }}
        />
      ))}
    </Flex>
  </div>
);

export const LocationAddressStreet: LocationComponent = ({
  location: loc,
  popup,
}) => {
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();

  return (
    <LocationAddressStyled
      $token={token}
      $breakpoints={breakpoints}
      $popup={popup}
    >
      {loc.geo.street}
    </LocationAddressStyled>
  );
};

export const LocationAddressCityRegion: LocationComponent = ({
  location: loc,
  popup,
}) => {
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();

  return (
    <LocationAddressStyled
      $token={token}
      $breakpoints={breakpoints}
      $popup={popup}
    >{`${loc.geo.city}, ${loc.geo.region} ${loc.geo.postal_code}`}</LocationAddressStyled>
  );
};

export const LocationHours: LocationComponent = ({ location: loc }) => {
  const breakpoints = Grid.useBreakpoint();

  const key = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ][new Date().getDay()] as keyof typeof loc.hours;

  const hoursObj = loc.hours[key];

  if (!breakpoints.sm) {
    return hoursObj?.open ? (
      <Flex gap="small" style={{ marginTop: "0.25rem" }}>
        <LocationHoursStatusStyled>{"Open Today:"}</LocationHoursStatusStyled>
        <LocationHoursTextStyled>{hoursObj?.text}</LocationHoursTextStyled>
      </Flex>
    ) : (
      <LocationHoursStatusStyled>{"Closed Today"}</LocationHoursStatusStyled>
    );
  }

  return hoursObj?.open ? (
    <React.Fragment>
      <LocationHoursStatusStyled>{"Open Today:"}</LocationHoursStatusStyled>
      <LocationHoursTextStyled>{hoursObj?.text}</LocationHoursTextStyled>
    </React.Fragment>
  ) : (
    <LocationHoursStatusStyled>{"Closed Today"}</LocationHoursStatusStyled>
  );
};

export const LocationGetDirections: React.FC<
  LocationCardProps & { distanceCenter?: google.maps.LatLngLiteral }
> = ({ location: loc, distanceCenter, popup }) => {
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();

  let distance;

  if (distanceCenter) {
    const from = new DDPoint(distanceCenter.lat, distanceCenter.lng);
    const to = new DDPoint(loc.geo.latitude, loc.geo.longitude);

    distance = Math.ceil(HaversineMiles.getDistance(from, to));
  }

  return (
    <LocationCtaStyled
      $token={token}
      $breakpoints={breakpoints}
      $popup={popup}
      href={loc.geo.get_directions_link}
      target="_blank"
      rel="noreferrer"
    >
      <Flex
        gap="small"
        justify="flex-start"
        align="center"
        style={{ height: "100%" }}
      >
        <ExternalLink
          height="1rem"
          color={token.colorPrimary}
          style={{ marginTop: "0.125rem" }}
        />
        {distance === undefined ? (
          <NoWrapText $token={token} $breakpoints={breakpoints} $popup={popup}>
            {"Get Directions"}
          </NoWrapText>
        ) : (
          <Flex gap={0} align="start" vertical={true}>
            <NoWrapText
              $token={token}
              $breakpoints={breakpoints}
              $popup={popup}
              style={{ lineHeight: "0.9375rem" }}
            >
              {"Get Directions"}
            </NoWrapText>
            <NoWrapText
              $token={token}
              $breakpoints={breakpoints}
              $popup={popup}
              style={{
                color: token.colorTextTertiary,
                lineHeight: "0.9375rem",
              }}
            >{`${Miles.format(distance)} away`}</NoWrapText>
          </Flex>
        )}
      </Flex>
    </LocationCtaStyled>
  );
};

export const LocationPhone: LocationComponent = ({ location: loc, popup }) => {
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();

  return (
    <LocationCtaStyled
      $token={token}
      $breakpoints={breakpoints}
      $popup={popup}
      href={`tel:${loc.phone.replace(/\D/g, "")}`}
    >
      <Flex
        gap="small"
        justify={popup && !breakpoints.sm ? "left-start" : "flex-end"}
        align="center"
        style={{ height: "100%" }}
      >
        <Phone
          fill={token.colorPrimary}
          height="1rem"
          style={{
            marginTop: "0.125rem",
          }}
        />
        <NoWrapText $token={token} $breakpoints={breakpoints} $popup={popup}>
          {loc.phone}
        </NoWrapText>
      </Flex>
    </LocationCtaStyled>
  );
};

export const LocationRentNowButton: LocationComponent = ({ popup }) => {
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();

  return (
    <LocationButtonStyled
      $token={token}
      $breakpoints={breakpoints}
      $popup={popup}
      href="/rent"
      target="_blank"
      type="primary"
    >
      {"Rent Now"}
    </LocationButtonStyled>
  );
};

export const LocationViewLocationButton: LocationComponent = ({
  location: loc,
  popup,
}) => {
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();

  if (!loc) {
    return null;
  }

  return (
    <LocationButtonStyled
      $token={token}
      $breakpoints={breakpoints}
      $popup={popup}
      href={`/locations/${loc.slug}`}
      target="_blank"
      type="primary"
      ghost={!popup}
      size={popup && !breakpoints.sm ? "small" : "middle"}
    >
      <Typography.Text>{"View Location"}</Typography.Text>
    </LocationButtonStyled>
  );
};
